// frontend-react/src/services/keeperProjectionService.js
import api from './apiService';

/**
 * Keeper Projection API Service
 * Handles all keeper projection-related API calls for future season planning
 * 8 endpoints for projecting keepers into next season
 */

export const keeperProjectionService = {
  /**
   * GET /api/leagues/{league_id}/teams/{team_id}/keeper-projections/selections
   * Get saved projected keeper selections
   */
  getProjectedKeeperSelections: async (leagueId, teamId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/selections`,
        { params: { include_stat_projections: true } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching projected keeper selections:', error);
      throw error;
    }
  },

  /**
   * GET /api/leagues/{league_id}/teams/{team_id}/keeper-projections/eligible
   * Get projected eligible keepers for next season
   */
  getProjectedEligibleKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/eligible`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching projected eligible keepers:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keeper-projections/save
   * Save projected keeper draft selections
   * Request: { keeper_selections: [{player_id, keeper_cost, contract_years_remaining}] }
   */
  saveProjectedKeeperDraft: async (leagueId, teamId, keeperSelections) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/save`,
        { keeper_selections: keeperSelections }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving projected keeper draft:', error);
      throw error;
    }
  },

  /**
   * GET /api/leagues/{league_id}/teams/{team_id}/keeper-projections/status
   * Get team's projected keeper financial status
   */
  getProjectedStatus: async (leagueId, teamId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/status`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching projected status:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keeper-projections/dead-money
   * Calculate dead money for projected keeper selections
   * Request: { selected_player_ids: [660271, 605141] }
   */
  calculateDeadMoney: async (leagueId, teamId, selectedPlayerIds) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/dead-money`,
        { selected_player_ids: selectedPlayerIds }
      );
      return response.data;
    } catch (error) {
      console.error('Error calculating dead money:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keeper-projections/finalize
   * Finalize projected keeper selections (saved → final)
   * SUBMIT button - locks in projected keepers
   */
  finalizeProjectedKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/finalize`
      );
      return response.data;
    } catch (error) {
      console.error('Error finalizing projected keepers:', error);
      throw error;
    }
  },

  /**
   * GET /api/leagues/{league_id}/teams/{team_id}/keeper-projections/history
   * Get keeper selection history (audit trail)
   */
  getKeeperHistory: async (leagueId, teamId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/history`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching keeper history:', error);
      throw error;
    }
  },

  /**
   * DELETE /api/leagues/{league_id}/teams/{team_id}/keeper-projections/clear
   * Clear all projected keeper selections (DESTRUCTIVE)
   */
  clearProjectedKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.delete(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/clear`
      );
      return response.data;
    } catch (error) {
      console.error('Error clearing projected keepers:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keeper-projections/copy-from-current
   * Copy current season keepers to projected season
   */
  copyCurrentKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-projections/copy-from-current`
      );
      return response.data;
    } catch (error) {
      console.error('Error copying current keepers:', error);
      throw error;
    }
  }
};

export default keeperProjectionService;
